import { css } from '@emotion/react';
import {
  Button,
  COLOR_PALETTE,
  Flex,
  Icon,
  Text,
  Title,
  desktopFirstMediaQuery,
  rem,
} from '@inflearn/ds-react-next';
import { faAngleRight, faArrowDownToLine } from '@inflearn/pro-regular-svg-icons';
import { faCircleExclamation } from '@inflearn/pro-solid-svg-icons';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useIsWebview } from '../../../hooks/useIsWebview';
import { GeneratedHtml } from '../../common/GeneratedHtml';

type PolicyContentLayoutProps = {
  title: string;
  html?: string;
  prevPolicy?: {
    href: string;
    buttonText?: string;
    description?: string;
  };
  download?: {
    href: string;
    buttonText: string;
  };
  isWebview?: boolean;
};

export const PolicyContentLayout = ({
  title,
  html,
  prevPolicy,
  download,
  isWebview: initialIsWebview,
}: PolicyContentLayoutProps) => {
  const isWebview = useIsWebview({ initialIsWebview });

  if (isNil(html)) {
    return null;
  }

  return (
    <Flex w="100%" direction="column" mb={64}>
      {!isWebview && <PolicyHeader title={title} prevPolicy={prevPolicy} download={download} />}
      <Flex
        w="100%"
        miw={0}
        styles={{
          root: stylePolicyContent,
        }}
      >
        <GeneratedHtml html={html} />
      </Flex>
    </Flex>
  );
};

const PolicyHeader = ({ title, prevPolicy, download }: Omit<PolicyContentLayoutProps, 'html'>) => {
  const { t } = useTranslation();
  const router = useRouter();
  const isPreviousPage = router.pathname.includes('/previous');

  return (
    <>
      {isPreviousPage && prevPolicy && (
        <Flex
          justify="space-between"
          align="center"
          mb={24}
          py={12}
          px={16}
          styles={{
            root: { borderRadius: '8px', backgroundColor: COLOR_PALETTE.blue[0] },
          }}
        >
          <Flex w={380} align="center" gap={8}>
            <Icon icon={faCircleExclamation} color="blue.8" size="md" />
            <Text fz={14} fw={700} c="blue.8">
              {prevPolicy.description}
            </Text>
          </Flex>
          <Button component={Link} href={prevPolicy.href} size="xs" color="blue">
            {t('현재 내용 보기')}
          </Button>
        </Flex>
      )}
      <Flex w="100%" wrap="wrap" justify="space-between" align="center" gap={12} mb={24}>
        <Title
          order={2}
          styles={{
            root: {
              [desktopFirstMediaQuery.mobile]: {
                fontSize: rem(22),
              },
            },
          }}
        >
          {title}
        </Title>
        {!isPreviousPage && prevPolicy && (
          <Link href={prevPolicy.href}>
            <Flex gap={4} align="center">
              <Text size="sm" fw={500} c="gray.6">
                {prevPolicy.buttonText}
              </Text>
              <Icon icon={faAngleRight} size="sm" color="gray.6" css={{ marginBottom: '1px' }} />
            </Flex>
          </Link>
        )}
        {download && (
          <a href={download.href} target="_blank" rel="noreferrer" download>
            <Flex gap={4} align="center">
              <Text size="sm" fw={500} c="gray.6">
                {download.buttonText}
              </Text>
              <Icon icon={faArrowDownToLine} size="sm" color="gray.6" />
            </Flex>
          </a>
        )}
      </Flex>
    </>
  );
};

export const stylePolicyList = css({
  '.content': {
    ul: {
      listStyle: 'disc',
    },

    ol: {
      listStyle: 'decimal',
      ol: {
        listStyle: 'lower-alpha',
        ol: {
          listStyle: 'lower-roman',
        },
      },
    },

    'ul, ol': {
      paddingTop: rem(8),
      paddingBottom: rem(16),
      paddingLeft: rem(16),

      display: 'flex',
      flexFlow: 'column wrap',
      gap: rem(8),
      width: '100%',
      minWidth: 0,
      [desktopFirstMediaQuery.mobile]: {
        marginLeft: 0,
      },
      'ul, ol': {
        marginBottom: rem(8),
        paddingTop: rem(16),
        paddingLeft: rem(32),
        paddingRight: rem(16),

        backgroundColor: COLOR_PALETTE.gray[0],
        borderRadius: rem(8),

        'ul, ol': {
          padding: 0,
          paddingLeft: rem(16),
          paddingRight: rem(16),
        },
      },
    },
  },
});

/**
 * 통합서비스 이용약관, 인프런 이용약관, 개인정보 처리방침
 * 파트너스 이용약관, 운영 적책 공통 스타일
 */
export const stylePolicyContent = css(
  {
    '.content': {
      fontSize: rem(16),
      lineHeight: rem(27.2),
      fontWeight: 400,
      color: COLOR_PALETTE.gray[9],

      a: {
        color: COLOR_PALETTE.blue[6],
        cursor: 'pointer',
        textDecorationLine: 'underline',
      },

      h3: {
        marginTop: rem(24),
        marginBottom: rem(8),
        fontSize: rem(22),
        lineHeight: rem(30.8),
        fontWeight: 700,
      },

      h4: {
        marginTop: rem(24),
        marginBottom: rem(8),
        fontSize: rem(18),
        lineHeight: rem(26.1),
        fontWeight: 700,
      },

      '.table__container': {
        overflowX: 'auto',
        minWidth: 0,
        width: '100%',
      },

      table: {
        fontSize: rem(12),
        lineHeight: 1.5,

        [desktopFirstMediaQuery.tablet]: {
          maxWidth: 'inherit',
        },
        'thead th': {
          border: `1px solid ${COLOR_PALETTE.gray[3]}`,
          backgroundColor: COLOR_PALETTE.gray[2],
          padding: `${rem(9)} ${rem(8)}`,
          whiteSpace: 'nowrap',
        },
        'tbody td': {
          border: `1px solid ${COLOR_PALETTE.gray[3]}`,
          backgroundColor: 'white',
          padding: `${rem(9)} ${rem(8)}`,
        },
      },
    },
  },
  stylePolicyList,
);
